/**
 * PointCloudAnimation.tsx
 *
 * Canvas-based point cloud animation for the login screen.
 * A realistic building (edges, walls, windows, roof) renders as a dense
 * point cloud, then dissolves into gently drifting particles.
 *
 * Phases:
 *   1. FORM    (0–3s)   — particles converge to building shape
 *   2. HOLD    (3–6s)   — building holds, subtle micro-vibration
 *   3. DISSOLVE (6–10s) — particles drift outward
 *   4. DRIFT   (10s+)   — infinite gentle wobble
 */

import { useEffect, useRef } from 'react'

// ── Building geometry ──────────────────────────────────────────────────────

interface Vec3 { x: number; y: number; z: number }

/** Place points along a line segment */
function edgePoints(a: Vec3, b: Vec3, density: number): Vec3[] {
  const pts: Vec3[] = []
  const dx = b.x - a.x, dy = b.y - a.y, dz = b.z - a.z
  const len = Math.sqrt(dx * dx + dy * dy + dz * dz)
  const n = Math.max(2, Math.round(len * density))
  for (let i = 0; i <= n; i++) {
    const t = i / n
    pts.push({ x: a.x + dx * t, y: a.y + dy * t, z: a.z + dz * t })
  }
  return pts
}

/** Place points on a rectangular face (surface scatter) */
function facePoints(
  corners: [Vec3, Vec3, Vec3, Vec3], // a, b, c, d — quad
  density: number,
): Vec3[] {
  const [a, b, , d] = corners
  const ab = { x: b.x - a.x, y: b.y - a.y, z: b.z - a.z }
  const ad = { x: d.x - a.x, y: d.y - a.y, z: d.z - a.z }
  const lenAB = Math.sqrt(ab.x ** 2 + ab.y ** 2 + ab.z ** 2)
  const lenAD = Math.sqrt(ad.x ** 2 + ad.y ** 2 + ad.z ** 2)
  const count = Math.round(lenAB * lenAD * density * density * 0.3)
  const pts: Vec3[] = []
  for (let i = 0; i < count; i++) {
    const u = Math.random(), v = Math.random()
    pts.push({
      x: a.x + ab.x * u + ad.x * v,
      y: a.y + ab.y * u + ad.y * v,
      z: a.z + ab.z * u + ad.z * v,
    })
  }
  return pts
}

/** Box defined by min/max, returns all 12 edges + 6 faces */
function boxPoints(
  x0: number, x1: number,
  y0: number, y1: number,
  z0: number, z1: number,
  edgeDensity: number,
  faceDensity: number,
): Vec3[] {
  const c = [
    { x: x0, y: y0, z: z0 }, // 0
    { x: x1, y: y0, z: z0 }, // 1
    { x: x1, y: y1, z: z0 }, // 2
    { x: x0, y: y1, z: z0 }, // 3
    { x: x0, y: y0, z: z1 }, // 4
    { x: x1, y: y0, z: z1 }, // 5
    { x: x1, y: y1, z: z1 }, // 6
    { x: x0, y: y1, z: z1 }, // 7
  ]
  const pts: Vec3[] = []

  // 12 edges
  const edges: [number, number][] = [
    [0, 1], [1, 2], [2, 3], [3, 0], // bottom
    [4, 5], [5, 6], [6, 7], [7, 4], // top
    [0, 4], [1, 5], [2, 6], [3, 7], // verticals
  ]
  for (const [a, b] of edges) {
    pts.push(...edgePoints(c[a], c[b], edgeDensity))
  }

  // 6 faces (lighter fill)
  const faces: [number, number, number, number][] = [
    [0, 1, 2, 3], // bottom
    [4, 5, 6, 7], // top
    [0, 1, 5, 4], // front
    [2, 3, 7, 6], // back
    [0, 3, 7, 4], // left
    [1, 2, 6, 5], // right
  ]
  for (const [a, b, cc, d] of faces) {
    pts.push(...facePoints([c[a], c[b], c[cc], c[d]], faceDensity))
  }

  return pts
}

/** Window: small recessed rectangle on a face */
function windowPoints(
  origin: Vec3,     // bottom-left of window
  uDir: Vec3,       // horizontal direction (unit * width)
  vDir: Vec3,       // vertical direction (unit * height)
  density: number,
): Vec3[] {
  const pts: Vec3[] = []
  // Just edges of the window opening
  const corners: Vec3[] = [
    origin,
    { x: origin.x + uDir.x, y: origin.y + uDir.y, z: origin.z + uDir.z },
    { x: origin.x + uDir.x + vDir.x, y: origin.y + uDir.y + vDir.y, z: origin.z + uDir.z + vDir.z },
    { x: origin.x + vDir.x, y: origin.y + vDir.y, z: origin.z + vDir.z },
  ]
  for (let i = 0; i < 4; i++) {
    pts.push(...edgePoints(corners[i], corners[(i + 1) % 4], density))
  }
  return pts
}

/** Generate a complex multi-volume building */
function generateBuilding(): Vec3[] {
  const pts: Vec3[] = []
  const ED = 14 // edge density
  const FD = 5  // face density

  // ── Main volume: L-shaped footprint ────────────────────────
  // Wing A (long bar)
  pts.push(...boxPoints(-3.5, 1.5, -2, 2, 0, 5.5, ED, FD))
  // Wing B (perpendicular, shorter)
  pts.push(...boxPoints(1.5, 4, -2, 0.5, 0, 4.5, ED, FD))

  // ── Floor slabs — Wing A ───────────────────────────────────
  for (let floor = 1; floor <= 4; floor++) {
    const z = floor * 1.375
    pts.push(...edgePoints({ x: -3.5, y: -2, z }, { x: 1.5, y: -2, z }, ED))
    pts.push(...edgePoints({ x: -3.5, y: 2, z }, { x: 1.5, y: 2, z }, ED))
    pts.push(...edgePoints({ x: -3.5, y: -2, z }, { x: -3.5, y: 2, z }, ED))
    pts.push(...edgePoints({ x: 1.5, y: -2, z }, { x: 1.5, y: 2, z }, ED))
  }

  // ── Floor slabs — Wing B ───────────────────────────────────
  for (let floor = 1; floor <= 3; floor++) {
    const z = floor * 1.5
    pts.push(...edgePoints({ x: 1.5, y: -2, z }, { x: 4, y: -2, z }, ED))
    pts.push(...edgePoints({ x: 1.5, y: 0.5, z }, { x: 4, y: 0.5, z }, ED))
    pts.push(...edgePoints({ x: 4, y: -2, z }, { x: 4, y: 0.5, z }, ED))
  }

  // ── Windows — Wing A front (y = -2) ───────────────────────
  for (let floor = 0; floor < 4; floor++) {
    const zBase = floor * 1.375 + 0.3
    for (let i = 0; i < 5; i++) {
      const xBase = -3.1 + i * 1.0
      pts.push(...windowPoints(
        { x: xBase, y: -2, z: zBase },
        { x: 0.6, y: 0, z: 0 },
        { x: 0, y: 0, z: 0.75 },
        ED * 0.5,
      ))
    }
  }

  // ── Windows — Wing A back (y = 2) ─────────────────────────
  for (let floor = 0; floor < 4; floor++) {
    const zBase = floor * 1.375 + 0.3
    for (let i = 0; i < 5; i++) {
      const xBase = -3.1 + i * 1.0
      pts.push(...windowPoints(
        { x: xBase, y: 2, z: zBase },
        { x: 0.6, y: 0, z: 0 },
        { x: 0, y: 0, z: 0.75 },
        ED * 0.5,
      ))
    }
  }

  // ── Windows — Wing A left (x = -3.5) ──────────────────────
  for (let floor = 0; floor < 4; floor++) {
    const zBase = floor * 1.375 + 0.3
    for (let i = 0; i < 3; i++) {
      const yBase = -1.5 + i * 1.1
      pts.push(...windowPoints(
        { x: -3.5, y: yBase, z: zBase },
        { x: 0, y: 0.6, z: 0 },
        { x: 0, y: 0, z: 0.75 },
        ED * 0.5,
      ))
    }
  }

  // ── Windows — Wing B front (y = -2) ───────────────────────
  for (let floor = 0; floor < 3; floor++) {
    const zBase = floor * 1.5 + 0.35
    for (let i = 0; i < 2; i++) {
      const xBase = 1.9 + i * 1.0
      pts.push(...windowPoints(
        { x: xBase, y: -2, z: zBase },
        { x: 0.6, y: 0, z: 0 },
        { x: 0, y: 0, z: 0.8 },
        ED * 0.5,
      ))
    }
  }

  // ── Windows — Wing B right (x = 4) ────────────────────────
  for (let floor = 0; floor < 3; floor++) {
    const zBase = floor * 1.5 + 0.35
    for (let i = 0; i < 2; i++) {
      const yBase = -1.5 + i * 1.0
      pts.push(...windowPoints(
        { x: 4, y: yBase, z: zBase },
        { x: 0, y: 0.6, z: 0 },
        { x: 0, y: 0, z: 0.8 },
        ED * 0.5,
      ))
    }
  }

  // ── Roof setback — Wing A penthouse ────────────────────────
  pts.push(...boxPoints(-2.8, 0.8, -1.4, 1.4, 5.5, 6.8, ED, FD * 0.7))

  // ── Parapet edges — Wing A ─────────────────────────────────
  pts.push(...edgePoints({ x: -3.5, y: -2, z: 5.65 }, { x: 1.5, y: -2, z: 5.65 }, ED))
  pts.push(...edgePoints({ x: -3.5, y: 2, z: 5.65 }, { x: 1.5, y: 2, z: 5.65 }, ED))
  pts.push(...edgePoints({ x: -3.5, y: -2, z: 5.65 }, { x: -3.5, y: 2, z: 5.65 }, ED))
  pts.push(...edgePoints({ x: 1.5, y: -2, z: 5.65 }, { x: 1.5, y: 2, z: 5.65 }, ED))

  // ── Parapet edges — Wing B ─────────────────────────────────
  pts.push(...edgePoints({ x: 1.5, y: -2, z: 4.65 }, { x: 4, y: -2, z: 4.65 }, ED))
  pts.push(...edgePoints({ x: 1.5, y: 0.5, z: 4.65 }, { x: 4, y: 0.5, z: 4.65 }, ED))
  pts.push(...edgePoints({ x: 4, y: -2, z: 4.65 }, { x: 4, y: 0.5, z: 4.65 }, ED))

  // ── Rooftop elements ───────────────────────────────────────
  // Elevator shaft
  pts.push(...boxPoints(-0.6, 0.4, -0.5, 0.5, 6.8, 7.8, ED * 0.7, FD * 0.4))
  // Mechanical unit
  pts.push(...boxPoints(-2.4, -1.4, -0.8, 0.3, 6.8, 7.3, ED * 0.6, FD * 0.3))

  // ── Balcony slabs — Wing A front ───────────────────────────
  for (let floor = 1; floor <= 3; floor++) {
    const z = floor * 1.375
    pts.push(...edgePoints({ x: -3.5, y: -2, z }, { x: -3.5, y: -2.6, z }, ED * 0.4))
    pts.push(...edgePoints({ x: 1.5, y: -2, z }, { x: 1.5, y: -2.6, z }, ED * 0.4))
    pts.push(...edgePoints({ x: -3.5, y: -2.6, z }, { x: 1.5, y: -2.6, z }, ED))
    // Balcony railing top
    const rz = z + 1.0
    pts.push(...edgePoints({ x: -3.5, y: -2.6, z: rz }, { x: 1.5, y: -2.6, z: rz }, ED * 0.8))
    pts.push(...edgePoints({ x: -3.5, y: -2.6, z }, { x: -3.5, y: -2.6, z: rz }, ED * 0.3))
    pts.push(...edgePoints({ x: 1.5, y: -2.6, z }, { x: 1.5, y: -2.6, z: rz }, ED * 0.3))
  }

  // ── Entrance canopy ────────────────────────────────────────
  pts.push(...edgePoints({ x: -1.5, y: -2, z: 2.8 }, { x: -1.5, y: -3.2, z: 2.8 }, ED * 0.5))
  pts.push(...edgePoints({ x: 0.5, y: -2, z: 2.8 }, { x: 0.5, y: -3.2, z: 2.8 }, ED * 0.5))
  pts.push(...edgePoints({ x: -1.5, y: -3.2, z: 2.8 }, { x: 0.5, y: -3.2, z: 2.8 }, ED))
  pts.push(...edgePoints({ x: -1.5, y: -2, z: 2.8 }, { x: 0.5, y: -2, z: 2.8 }, ED * 0.5))
  // Canopy columns
  pts.push(...edgePoints({ x: -1.5, y: -3.2, z: 0 }, { x: -1.5, y: -3.2, z: 2.8 }, ED * 0.5))
  pts.push(...edgePoints({ x: 0.5, y: -3.2, z: 0 }, { x: 0.5, y: -3.2, z: 2.8 }, ED * 0.5))

  // ── Staircase tower — attached to Wing B back ──────────────
  pts.push(...boxPoints(2.2, 3.2, 0.5, 1.5, 0, 4.5, ED * 0.7, FD * 0.4))
  // Stair windows (narrow, vertical)
  for (let floor = 0; floor < 3; floor++) {
    const zBase = floor * 1.5 + 0.2
    pts.push(...windowPoints(
      { x: 2.2, y: 0.7, z: zBase },
      { x: 0, y: 0.3, z: 0 },
      { x: 0, y: 0, z: 1.1 },
      ED * 0.4,
    ))
  }

  // ── Ground plane — sparse grid ─────────────────────────────
  for (let x = -5; x <= 5.5; x += 0.6) {
    pts.push(...edgePoints({ x, y: -4, z: 0 }, { x, y: 3.5, z: 0 }, 3))
  }
  for (let y = -4; y <= 3.5; y += 0.6) {
    pts.push(...edgePoints({ x: -5, y, z: 0 }, { x: 5.5, y, z: 0 }, 3))
  }

  return pts
}

// ── Noise helpers ──────────────────────────────────────────────────────────

function noise2D(x: number, y: number): number {
  const n = Math.sin(x * 12.9898 + y * 78.233) * 43758.5453
  return (n - Math.floor(n)) * 2 - 1
}

function smoothNoise(x: number, y: number): number {
  const ix = Math.floor(x), iy = Math.floor(y)
  const fx = x - ix, fy = y - iy
  const sx = fx * fx * (3 - 2 * fx), sy = fy * fy * (3 - 2 * fy)
  const a = noise2D(ix, iy), b = noise2D(ix + 1, iy)
  const c = noise2D(ix, iy + 1), d = noise2D(ix + 1, iy + 1)
  return a + (b - a) * sx + (c - a) * sy + (a - b - c + d) * sx * sy
}

// ── Isometric projection ───────────────────────────────────────────────────

function iso(x: number, y: number, z: number, cx: number, cy: number, s: number): [number, number] {
  const px = cx + (x - y) * s * 0.866
  const py = cy - z * s + (x + y) * s * 0.5
  return [px, py]
}

// ── Component ──────────────────────────────────────────────────────────────

const DPR = typeof window !== 'undefined' ? Math.min(window.devicePixelRatio || 1, 2) : 1

export function PointCloudAnimation() {
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const rafRef = useRef<number>(0)

  useEffect(() => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    function resize() {
      const rect = canvas!.getBoundingClientRect()
      canvas!.width = rect.width * DPR
      canvas!.height = rect.height * DPR
    }
    resize()
    window.addEventListener('resize', resize)

    // Generate building geometry
    const building = generateBuilding()
    const PARTICLE_COUNT = building.length

    // Particle state
    // Phase: SEA (0–2s) → FORM (2–6s) → WOBBLE (6s+ infinite)
    const particles = building.map((target) => {
      // Start: scattered sea of points across the entire canvas area
      const spread = 16
      return {
        // Sea position (random spread)
        sx: (Math.random() - 0.5) * spread,
        sy: (Math.random() - 0.5) * spread,
        sz: (Math.random() - 0.5) * spread * 0.5 + 3,
        // Target (building)
        tx: target.x,
        ty: target.y,
        tz: target.z,
        // Per-particle stagger
        delay: Math.random() * 1.2,
        // Noise seed
        seed: Math.random() * 100,
        // Size
        size: 0.8 + Math.random() * 1.0,
      }
    })

    const startTime = performance.now()

    function animate(now: number) {
      const t = (now - startTime) / 1000
      const w = canvas!.width
      const h = canvas!.height

      ctx!.clearRect(0, 0, w, h)

      const cx = w * 0.5
      const cy = h * 0.62
      const scale = Math.min(w, h) * 0.075

      // ── Perspective ground grid ──────────────────────────────────
      // Isometric-projected grid on the ground plane (z=0)
      ctx!.strokeStyle = 'rgba(0, 0, 0, 0.08)'
      ctx!.lineWidth = DPR * 0.5

      const gridStep = 1.5
      const gridExtent = 18

      // Lines along X (varying y)
      for (let y = -gridExtent; y <= gridExtent; y += gridStep) {
        const [x0, y0] = iso(-gridExtent, y, 0, cx, cy, scale)
        const [x1, y1] = iso(gridExtent, y, 0, cx, cy, scale)
        ctx!.beginPath()
        ctx!.moveTo(x0, y0)
        ctx!.lineTo(x1, y1)
        ctx!.stroke()
      }

      // Lines along Y (varying x)
      for (let x = -gridExtent; x <= gridExtent; x += gridStep) {
        const [x0, y0] = iso(x, -gridExtent, 0, cx, cy, scale)
        const [x1, y1] = iso(x, gridExtent, 0, cx, cy, scale)
        ctx!.beginPath()
        ctx!.moveTo(x0, y0)
        ctx!.lineTo(x1, y1)
        ctx!.stroke()
      }

      // Very slow rotation
      const rot = t * 0.03

      for (let i = 0; i < PARTICLE_COUNT; i++) {
        const p = particles[i]
        const pt = t - p.delay

        let px: number, py: number, pz: number

        if (pt < 0) {
          // Still in sea, drifting gently
          const seaDrift = 0.3
          px = p.sx + smoothNoise(p.seed + t * 0.2, 0) * seaDrift
          py = p.sy + smoothNoise(0, p.seed + t * 0.2) * seaDrift
          pz = p.sz + smoothNoise(p.seed, t * 0.15) * seaDrift * 0.5
        } else if (pt < 4) {
          // FORM — converge from sea to building
          const prog = Math.min(1, pt / 3.5)
          const ease = prog < 0.5
            ? 4 * prog * prog * prog
            : 1 - Math.pow(-2 * prog + 2, 3) / 2
          px = p.sx + (p.tx - p.sx) * ease
          py = p.sy + (p.ty - p.sy) * ease
          pz = p.sz + (p.tz - p.sz) * ease
        } else {
          // WOBBLE — building shape with gentle infinite drift
          const wobbleT = pt - 4
          const wobble = 0.04 + Math.min(wobbleT * 0.003, 0.08)
          px = p.tx + smoothNoise(p.seed + wobbleT * 0.1, wobbleT * 0.07) * wobble
          py = p.ty + smoothNoise(wobbleT * 0.07, p.seed + wobbleT * 0.1) * wobble
          pz = p.tz + smoothNoise(p.seed * 2, wobbleT * 0.08) * wobble * 0.5
        }

        // Rotate
        const cosR = Math.cos(rot), sinR = Math.sin(rot)
        const rx = px * cosR - py * sinR
        const ry = px * sinR + py * cosR

        // Project
        const [screenX, screenY] = iso(rx, ry, pz, cx, cy, scale)

        // Depth-based alpha & size
        const depth = (ry + 5) / 10
        const alpha = 0.15 + Math.max(0, Math.min(1, depth)) * 0.5
        const sz = p.size * DPR * (0.6 + Math.max(0, depth) * 0.5)

        ctx!.beginPath()
        ctx!.arc(screenX, screenY, sz, 0, Math.PI * 2)
        ctx!.fillStyle = `rgba(0, 0, 0, ${alpha})`
        ctx!.fill()
      }

      rafRef.current = requestAnimationFrame(animate)
    }

    rafRef.current = requestAnimationFrame(animate)

    return () => {
      cancelAnimationFrame(rafRef.current)
      window.removeEventListener('resize', resize)
    }
  }, [])

  return (
    <canvas
      ref={canvasRef}
      style={{ width: '100%', height: '100%', display: 'block' }}
    />
  )
}
